import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  XMarkIcon,
  CloudArrowUpIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { api } from '../../services/api'

interface BulkUploadModalProps {
  open: boolean
  onClose: () => void
  onCompleted: () => void
}

interface FailedRow {
  row: number
  email: string | null
  reasons: string[]
}

interface BulkUploadResult {
  totalRows: number
  created: number
  failed: FailedRow[]
  /** Created rows using a generated temp password; credential emails dispatched. */
  credentialsQueued?: number
  /** Created rows whose admin-supplied password was honored (no email sent). */
  emailsSkipped?: number
}

const CSV_TEMPLATE_HEADERS = [
  'firstName',
  'lastName',
  'email',
  'password',
  'phone',
  'nationality',
  'dateOfBirth',
  'gender',
  'address',
  'city',
  'state',
  'postalCode',
  'passportNumber',
]

const CSV_EXAMPLE_ROWS = [
  [
    'Ahmad',
    'Bin Ismail',
    'ahmad.ismail@example.com',
    'StrongPass#2026',
    '+60123456789',
    'Malaysian',
    '1985-06-15',
    'MALE',
    '123 Jalan Ampang',
    'Kuala Lumpur City',
    'WP Kuala Lumpur',
    '50450',
    'A12345678',
  ],
  [
    'Siti',
    'Aminah',
    'siti.aminah@example.com',
    '',
    '0123456789',
    'Malaysian',
    '',
    'FEMALE',
    '',
    '',
    '',
    '',
    '',
  ],
  [
    'Wei Ming',
    'Tan',
    'wei.ming@example.com',
    '',
    '+6581234567',
    'Singaporean',
    '1990-11-02',
    'MALE',
    '',
    '',
    '',
    '',
    '',
  ],
]

function downloadCsv(filename: string, rows: (string | number | null)[][]) {
  const escapeCell = (v: string | number | null) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = rows.map((r) => r.map(escapeCell).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function BulkUploadModal({ open, onClose, onCompleted }: BulkUploadModalProps) {
  const { t } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<BulkUploadResult | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  if (!open) return null

  const reset = () => {
    setFile(null)
    setResult(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleClose = () => {
    if (uploading) return
    reset()
    onClose()
  }

  const downloadTemplate = () => {
    downloadCsv('bulk-user-upload-template.csv', [CSV_TEMPLATE_HEADERS, ...CSV_EXAMPLE_ROWS])
  }

  const downloadErrors = () => {
    if (!result?.failed.length) return
    downloadCsv('bulk-upload-errors.csv', [
      ['row', 'email', 'reasons'],
      ...result.failed.map((f) => [f.row, f.email ?? '', f.reasons.join('; ')]),
    ])
  }

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > 2 * 1024 * 1024) {
      toast.error(t('userManagement.bulkUpload.fileTooLarge', 'File must be smaller than 2 MB'))
      e.target.value = ''
      return
    }
    const okTypes = ['text/csv', 'application/vnd.ms-excel', 'text/plain', '']
    if (!okTypes.includes(f.type) && !f.name.toLowerCase().endsWith('.csv')) {
      toast.error(t('userManagement.bulkUpload.invalidFile', 'Only CSV files are allowed'))
      e.target.value = ''
      return
    }
    setFile(f)
    setResult(null)
  }

  const upload = async () => {
    if (!file) {
      toast.error(t('userManagement.bulkUpload.noFile', 'Please select a CSV file first'))
      return
    }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.upload<BulkUploadResult>('/admin/users/bulk-upload', formData)
      setResult(data)
      if (data.failed.length === 0) {
        toast.success(
          t('userManagement.bulkUpload.resultSummary', {
            defaultValue: '{{created}} of {{total}} users created',
            created: data.created,
            total: data.totalRows,
          }),
        )
      } else {
        toast.error(
          t('userManagement.bulkUpload.partialError', {
            defaultValue: '{{created}} created, {{failed}} failed',
            created: data.created,
            failed: data.failed.length,
          }),
        )
      }
      if (data.created > 0) onCompleted()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('userManagement.bulkUpload.title', 'Bulk Upload Users')}
            </h2>
            <p className="text-xs text-gray-500">
              {t('userManagement.bulkUpload.subtitle', 'Create regular users from a CSV file (max 500 rows)')}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            disabled={uploading}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          {/* Template download */}
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm text-gray-700 mb-3">
              {t(
                'userManagement.bulkUpload.templateHint',
                'Download the template, fill in one user per row, then upload it below. Required columns: firstName, lastName, email, phone, nationality. Optional: password (leave blank to email each user a temporary password). Phone accepts Malaysian (+60123456789 or 0123456789) and Singaporean (+6581234567 or 81234567) numbers.',
              )}
            </p>
            <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
              {t(
                'userManagement.bulkUpload.emailNote',
                'Each new user receives an email with their login email and a temporary password, and must set their own password at first login. You no longer need to include passwords in the CSV (an optional password column is still accepted for existing spreadsheets — those users will not be emailed).',
              )}
            </div>
            <button
              type="button"
              onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-100"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {t('userManagement.bulkUpload.downloadTemplate', 'Download Template (CSV)')}
            </button>
          </div>

          {/* File picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('userManagement.bulkUpload.selectFile', 'Select CSV file')}
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onPickFile}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 border border-gray-300 rounded-lg px-3 py-2 file:mr-3 file:rounded-md file:border-0 file:bg-[#0D7330] file:px-3 file:py-1.5 file:text-white file:text-xs hover:file:bg-[#0a5c27] disabled:opacity-60"
            />
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-3">
              <div
                className={`rounded-md px-4 py-3 text-sm ${
                  result.failed.length === 0
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-amber-50 border border-amber-200 text-amber-800'
                }`}
              >
                {t('userManagement.bulkUpload.resultSummary', {
                  defaultValue: '{{created}} of {{total}} users created',
                  created: result.created,
                  total: result.totalRows,
                })}
                {typeof result.credentialsQueued === 'number' && result.credentialsQueued > 0 && (
                  <> — {t('userManagement.bulkUpload.credentialsEmailed', {
                    defaultValue: 'login credentials emailed to {{count}} user(s); they must set their own password at first login',
                    count: result.credentialsQueued,
                  })}</>
                )}
              </div>

              {result.failed.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900">
                      {t('userManagement.bulkUpload.failedRows', 'Failed rows')}
                    </h3>
                    <button
                      type="button"
                      onClick={downloadErrors}
                      className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline"
                    >
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      {t('userManagement.bulkUpload.downloadErrors', 'Download error report (CSV)')}
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('userManagement.bulkUpload.row', 'Row')}
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('userManagement.bulkUpload.email', 'Email')}
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('userManagement.bulkUpload.reasons', 'Reasons')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {result.failed.map((f) => (
                          <tr key={f.row}>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">{f.row}</td>
                            <td className="px-3 py-2 text-gray-900">{f.email || '—'}</td>
                            <td className="px-3 py-2 text-red-600">{f.reasons.join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {t('userManagement.bulkUpload.close', 'Close')}
            </button>
            <button
              type="button"
              onClick={upload}
              disabled={uploading || !file}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0D7330] px-5 py-2 text-sm font-medium text-white hover:bg-[#0a5c27] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CloudArrowUpIcon className="h-4 w-4" />
              {uploading
                ? t('userManagement.bulkUpload.uploading', 'Uploading...')
                : t('userManagement.bulkUpload.upload', 'Upload')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
